import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { registerCustomer, registerShop, loginUser } from '../utils/api';

/* ─────────────────────────────────────────
   Tiny Leaflet map picker (no API key needed)
   Uses OpenStreetMap tiles + Nominatim geocoding
───────────────────────────────────────── */
function MapPicker({ onSelect, initialLat = 12.9716, initialLng = 77.5946 }) {
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const mapInstanceRef = useRef(null);

  useEffect(() => {
    if (mapInstanceRef.current) return; // already init
    // Dynamically load Leaflet CSS + JS
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
    script.onload = () => initMap();
    document.head.appendChild(script);
    return () => {};
  }, []);

  const initMap = () => {
    const L = window.L;
    const map = L.map(mapRef.current).setView([initialLat, initialLng], 13);
    mapInstanceRef.current = map;
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors'
    }).addTo(map);

    const marker = L.marker([initialLat, initialLng], { draggable: true }).addTo(map);
    markerRef.current = marker;
    onSelect({ lat: initialLat, lng: initialLng });

    marker.on('dragend', (e) => {
      const { lat, lng } = e.target.getLatLng();
      onSelect({ lat, lng });
    });
    map.on('click', (e) => {
      const { lat, lng } = e.latlng;
      marker.setLatLng([lat, lng]);
      onSelect({ lat, lng });
    });
  };

  return (
    <div>
      <div ref={mapRef} style={{ height: 220, borderRadius: 12, border: '1.5px solid var(--border)', overflow: 'hidden', zIndex: 0 }} />
      <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 6 }}>
        📍 Click on the map or drag the pin to set your location
      </p>
    </div>
  );
}

/* ─────────────────────────────────────────
   Shared location section used in both forms
───────────────────────────────────────── */
const SERVICES_LIST = [
  { id: 'washing', label: '🫧 Washing' },
  { id: 'dry_cleaning', label: '👔 Dry Cleaning' },
  { id: 'laundry', label: '🧺 Full Laundry' },
  { id: 'ironing', label: '♨️ Ironing' },
];

function LocationSection({ form, setForm, required = false }) {
  const [showMap, setShowMap] = useState(false);
  const [detecting, setDetecting] = useState(false);

  const detectGPS = () => {
    setDetecting(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setForm(f => ({
          ...f,
          location: { type: 'Point', coordinates: [pos.coords.longitude, pos.coords.latitude] }
        }));
        setDetecting(false);
        setShowMap(false);
      },
      () => { alert('Location access denied. Enter pincode instead.'); setDetecting(false); }
    );
  };

  const hasLocation = form.location && form.location.coordinates[0] !== 0;

  return (
    <div className="form-group">
      <label className="form-label">Location {required ? '*' : '(optional)'}</label>

      {/* Location method buttons */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
        <button type="button" className={`btn btn-sm ${showMap ? 'btn-primary' : 'btn-outline'}`}
          onClick={() => setShowMap(!showMap)}>
          🗺 Pick on Map
        </button>
        <button type="button" className="btn btn-outline btn-sm" onClick={detectGPS} disabled={detecting}>
          {detecting ? '📡 Detecting...' : '📍 Use GPS'}
        </button>
      </div>

      {/* Map */}
      {showMap && (
        <div style={{ marginBottom: 10 }}>
          <MapPicker
            onSelect={({ lat, lng }) =>
              setForm(f => ({ ...f, location: { type: 'Point', coordinates: [lng, lat] } }))
            }
            initialLat={form.location?.coordinates[1] || 12.9716}
            initialLng={form.location?.coordinates[0] || 77.5946}
          />
        </div>
      )}

      {/* Pincode */}
      <input className="form-control" placeholder="OR enter Pincode (e.g. 560001)"
        value={form.pincode || ''} maxLength={6}
        onChange={e => setForm(f => ({ ...f, pincode: e.target.value.replace(/\D/g, '') }))} />

      {hasLocation && (
        <div style={{ marginTop: 6, padding: '6px 10px', background: '#d1fae5', borderRadius: 8, fontSize: '0.78rem', color: '#065f46' }}>
          ✅ Map location set: {form.location.coordinates[1].toFixed(4)}, {form.location.coordinates[0].toFixed(4)}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────
   Password strength indicator
───────────────────────────────────────── */
function PasswordStrength({ password }) {
  const checks = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
  ];
  const score = checks.filter(Boolean).length;
  const colors = ['#ef4444', '#f97316', '#f59e0b', '#10b981'];
  const labels = ['Weak', 'Fair', 'Good', 'Strong'];
  if (!password) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[0,1,2,3].map(i => (
          <div key={i} style={{ flex: 1, height: 3, borderRadius: 2, background: i < score ? colors[score - 1] : 'var(--border)', transition: 'background 0.3s' }} />
        ))}
      </div>
      <span style={{ fontSize: '0.75rem', color: colors[score - 1] }}>{labels[score - 1]}</span>
    </div>
  );
}

/* ─────────────────────────────────────────
   Customer Register Form
───────────────────────────────────────── */
function CustomerRegisterForm({ onSuccess, onSwitch }) {
  const [form, setForm] = useState({ name: '', phone: '', password: '', confirm: '', address: '', pincode: '', location: null });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError('Passwords do not match');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    setLoading(true); setError('');
    try {
      const res = await registerCustomer({
        name: form.name, phone: form.phone, password: form.password,
        address: form.address, pincode: form.pincode, location: form.location
      });
      onSuccess(res.data.token, res.data.user);
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}
      <div className="form-group">
        <label className="form-label">Full Name *</label>
        <input className="form-control" placeholder="Your full name"
          value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
      </div>
      <div className="form-group">
        <label className="form-label">Phone Number *</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ padding: '11px 12px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>🇮🇳 +91</span>
          <input className="form-control" placeholder="9876543210" maxLength={10}
            value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '') }))} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Password *</label>
        <div style={{ position: 'relative' }}>
          <input className="form-control" type={showPw ? 'text' : 'password'} placeholder="Min. 6 characters"
            value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
            style={{ paddingRight: 44 }} />
          <button type="button" onClick={() => setShowPw(s => !s)}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>
            {showPw ? '🙈' : '👁'}
          </button>
        </div>
        <PasswordStrength password={form.password} />
      </div>
      <div className="form-group">
        <label className="form-label">Confirm Password *</label>
        <input className="form-control" type="password" placeholder="Repeat password"
          value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
        {form.confirm && form.password !== form.confirm && (
          <span style={{ fontSize: '0.78rem', color: 'var(--danger)' }}>Passwords don't match</span>
        )}
      </div>
      <div className="form-group">
        <label className="form-label">Address</label>
        <input className="form-control" placeholder="Your home address"
          value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
      </div>
      <LocationSection form={form} setForm={setForm} />
      <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}>
        {loading ? 'Creating account...' : 'Create Account →'}
      </button>
      <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.88rem', color: 'var(--text-muted)' }}>
        Already have an account? <button type="button" onClick={onSwitch}
          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}>
          Sign in
        </button>
      </p>
    </form>
  );
}

/* ─────────────────────────────────────────
   Shop Register Form
───────────────────────────────────────── */
function ShopRegisterForm({ onSuccess, onSwitch }) {
  const [form, setForm] = useState({
    shop_name: '', owner_name: '', phone: '', password: '', confirm: '',
    address: '', pincode: '', description: '', services: [], location: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const toggleService = (id) =>
    setForm(f => ({ ...f, services: f.services.includes(id) ? f.services.filter(s => s !== id) : [...f.services, id] }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return setError('Passwords do not match');
    if (form.password.length < 6) return setError('Password must be at least 6 characters');
    if (!form.services.length) return setError('Select at least one service');
    if (!form.pincode && !form.location) return setError('Enter a pincode or pick location on map');
    setLoading(true); setError('');
    try {
      const res = await registerShop({
        shop_name: form.shop_name, owner_name: form.owner_name,
        phone: form.phone, password: form.password,
        address: form.address, pincode: form.pincode,
        description: form.description, services: form.services,
        location: form.location
      });
      onSuccess(res.data.token, res.data.user);
    } catch (e) {
      setError(e.response?.data?.detail || 'Registration failed');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit} style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 4 }}>
      {error && <div className="alert alert-error">{error}</div>}

      <div style={{ padding: '8px 12px', background: 'var(--accent-light)', borderRadius: 10, marginBottom: 14, fontSize: '0.83rem', color: '#92400e' }}>
        🏪 Shop registration — creates both your account and shop profile
      </div>

      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Shop Name *</label>
          <input className="form-control" placeholder="e.g. Fresh Laundry"
            value={form.shop_name} onChange={e => setForm(f => ({ ...f, shop_name: e.target.value }))} required />
        </div>
        <div className="form-group">
          <label className="form-label">Owner Name *</label>
          <input className="form-control" placeholder="Your full name"
            value={form.owner_name} onChange={e => setForm(f => ({ ...f, owner_name: e.target.value }))} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Phone Number *</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ padding: '11px 12px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>🇮🇳 +91</span>
          <input className="form-control" placeholder="9876543210" maxLength={10}
            value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '') }))} required />
        </div>
      </div>
      <div className="grid-2">
        <div className="form-group">
          <label className="form-label">Password *</label>
          <div style={{ position: 'relative' }}>
            <input className="form-control" type={showPw ? 'text' : 'password'} placeholder="Min. 6 chars"
              value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required style={{ paddingRight: 40 }} />
            <button type="button" onClick={() => setShowPw(s => !s)}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer' }}>
              {showPw ? '🙈' : '👁'}
            </button>
          </div>
          <PasswordStrength password={form.password} />
        </div>
        <div className="form-group">
          <label className="form-label">Confirm Password *</label>
          <input className="form-control" type="password" placeholder="Repeat password"
            value={form.confirm} onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))} required />
        </div>
      </div>
      <div className="form-group">
        <label className="form-label">Shop Address *</label>
        <input className="form-control" placeholder="Full address of your shop"
          value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} required />
      </div>
      <div className="form-group">
        <label className="form-label">Description</label>
        <textarea className="form-control" placeholder="Tell customers about your services..."
          rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
      </div>
      <div className="form-group">
        <label className="form-label">Services Offered *</label>
        <div className="service-options">
          {SERVICES_LIST.map(s => (
            <div key={s.id} className={`service-chip ${form.services.includes(s.id) ? 'selected' : ''}`}
              onClick={() => toggleService(s.id)}>{s.label}</div>
          ))}
        </div>
      </div>
      <LocationSection form={form} setForm={setForm} required />
      <button className="btn btn-accent btn-full btn-lg" type="submit" disabled={loading} style={{ marginTop: 4 }}>
        {loading ? 'Registering shop...' : '🏪 Register My Shop →'}
      </button>
      <p style={{ textAlign: 'center', marginTop: 14, fontSize: '0.88rem', color: 'var(--text-muted)' }}>
        Already registered? <button type="button" onClick={onSwitch}
          style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}>
          Sign in
        </button>
      </p>
    </form>
  );
}

/* ─────────────────────────────────────────
   Login Form
───────────────────────────────────────── */
function LoginForm({ onSuccess, onSwitchCustomer, onSwitchShop }) {
  const [form, setForm] = useState({ phone: '', password: '', role: 'customer' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPw, setShowPw] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await loginUser({ phone: form.phone, password: form.password, role: form.role });
      onSuccess(res.data.token, res.data.user);
    } catch (e) {
      setError(e.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && <div className="alert alert-error">{error}</div>}

      {/* Role selector */}
      <div className="form-group">
        <label className="form-label">I am a</label>
        <div style={{ display: 'flex', gap: 8 }}>
          {[{ v: 'customer', l: '👕 Customer' }, { v: 'laundry_owner', l: '🏪 Shop Owner' }].map(r => (
            <button key={r.v} type="button"
              style={{
                flex: 1, padding: '10px 8px', borderRadius: 10, cursor: 'pointer',
                border: `2px solid ${form.role === r.v ? 'var(--primary)' : 'var(--border)'}`,
                background: form.role === r.v ? 'var(--primary-light)' : 'white',
                color: form.role === r.v ? 'var(--primary-dark)' : 'var(--text-muted)',
                fontWeight: 600, fontSize: '0.88rem', fontFamily: 'DM Sans, sans-serif', transition: 'all 0.2s'
              }}
              onClick={() => setForm(f => ({ ...f, role: r.v }))}>{r.l}</button>
          ))}
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Phone Number *</label>
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ padding: '11px 12px', border: '1.5px solid var(--border)', borderRadius: 10, background: 'var(--bg)', fontSize: '0.9rem', whiteSpace: 'nowrap' }}>🇮🇳 +91</span>
          <input className="form-control" placeholder="9876543210" maxLength={10}
            value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value.replace(/\D/g, '') }))} required />
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Password *</label>
        <div style={{ position: 'relative' }}>
          <input className="form-control" type={showPw ? 'text' : 'password'} placeholder="Your password"
            value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required
            style={{ paddingRight: 44 }} />
          <button type="button" onClick={() => setShowPw(s => !s)}
            style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem' }}>
            {showPw ? '🙈' : '👁'}
          </button>
        </div>
      </div>

      <button className="btn btn-primary btn-full btn-lg" type="submit" disabled={loading}>
        {loading ? 'Signing in...' : 'Sign In →'}
      </button>

      <div style={{ textAlign: 'center', marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
        <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 10 }}>Don't have an account?</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button type="button" className="btn btn-outline btn-sm" onClick={onSwitchCustomer}>
            👕 Customer Sign Up
          </button>
          <button type="button" className="btn btn-outline btn-sm" onClick={onSwitchShop}>
            🏪 Shop Sign Up
          </button>
        </div>
      </div>
    </form>
  );
}

/* ─────────────────────────────────────────
   Main LoginPage
───────────────────────────────────────── */
export default function LoginPage() {
  // mode: 'login' | 'register-customer' | 'register-shop'
  const [mode, setMode] = useState('login');
  const { login } = useAuth();
  const navigate = useNavigate();

  const TITLES = {
    'login': { title: 'Welcome back', sub: 'Sign in to your account' },
    'register-customer': { title: 'Create Account', sub: 'Sign up as a customer' },
    'register-shop': { title: 'Register Your Shop', sub: 'Join as a laundry shop owner' },
  };

  const handleSuccess = (token, user) => {
    login(token, user);
    navigate(user.role === 'customer' ? '/customer/dashboard' : '/owner/requests');
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0f172a 0%, #075985 50%, #0ea5e9 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1.5rem'
    }}>
      <div style={{
        background: 'white', borderRadius: 24, padding: '2rem',
        width: '100%', maxWidth: mode === 'register-shop' ? 560 : 440,
        boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
        transition: 'max-width 0.3s ease'
      }}>
        {/* Logo + title */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{ fontSize: '2.2rem', marginBottom: 6 }}>🫧</div>
          <h2 style={{ fontFamily: 'poppins', fontSize: '1.6rem', fontWeight: 800, marginBottom: 4 }}>
            WashWave
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>
            {TITLES[mode].sub}
          </p>
        </div>

        {/* Tab pills for login page */}
        {mode === 'login' && (
          <div style={{ display: 'flex', background: 'var(--bg)', borderRadius: 12, padding: 4, marginBottom: 20 }}>
            <button style={{
              flex: 1, padding: '8px', borderRadius: 9, border: 'none', fontFamily: 'DM Sans, sans-serif',
              fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', transition: 'all 0.2s',
              background: 'white', color: 'var(--primary)', boxShadow: 'var(--shadow)'
            }}>Sign In</button>
          </div>
        )}

        {/* Forms */}
        {mode === 'login' && (
          <LoginForm
            onSuccess={handleSuccess}
            onSwitchCustomer={() => setMode('register-customer')}
            onSwitchShop={() => setMode('register-shop')}
          />
        )}
        {mode === 'register-customer' && (
          <CustomerRegisterForm
            onSuccess={handleSuccess}
            onSwitch={() => setMode('login')}
          />
        )}
        {mode === 'register-shop' && (
          <ShopRegisterForm
            onSuccess={handleSuccess}
            onSwitch={() => setMode('login')}
          />
        )}
      </div>
    </div>
  );
}
